/**
 * Payments & Invoices (/billing)
 *
 * The customer's running billing history: every checkout's itemized tax invoice
 * (view / download PDF / print) plus the billing-profile editor that feeds the
 * buyer block on future invoices. Themed + RTL, mirroring /account.
 *
 * The subscription + auto-renew card is added by Phase 7 (lifecycle).
 */
import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';
import { useToast } from '../../components/common/ToastProvider';
import invoiceService, {
  type InvoiceSummary,
  type BillingProfile,
  type InvoiceStatus,
  type SubscriptionSummary,
} from '../../services/invoiceService';
import { formatAmount, latnLocale } from '../../utils/numberFormat';

const STATUS_STYLES: Record<InvoiceStatus, string> = {
  PAID: 'bg-green-100 text-green-700',
  ISSUED: 'bg-amber-100 text-amber-700',
  REFUNDED: 'bg-gray-100 text-gray-600',
  PARTIALLY_REFUNDED: 'bg-orange-100 text-orange-700',
  CANCELLED: 'bg-gray-100 text-gray-500',
};

const SUB_STATUS_STYLES: Record<SubscriptionSummary['status'], string> = {
  ACTIVE: 'bg-green-100 text-green-700',
  PAST_DUE: 'bg-orange-100 text-orange-700',
  EXPIRED: 'bg-red-100 text-red-700',
  CANCELLED: 'bg-gray-100 text-gray-500',
};

const EMPTY_PROFILE: BillingProfile = {
  legalName: '',
  buyerType: 'INDIVIDUAL',
  vatNumber: '',
  crNumber: '',
  address: '',
  billingEmail: '',
};

export default function BillingPage() {
  const { t, i18n } = useTranslation(['billing', 'common']);
  const { session } = useAuth();
  const { showToast } = useToast();
  const isRTL = i18n.dir() === 'rtl';
  const locale = i18n.language === 'ar' ? 'ar-SA' : 'en-US';
  const token = session?.token ?? null;

  const [invoices, setInvoices] = useState<InvoiceSummary[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  const [profile, setProfile] = useState<BillingProfile>(EMPTY_PROFILE);
  const [savingProfile, setSavingProfile] = useState(false);

  const [subscription, setSubscription] = useState<SubscriptionSummary | null>(null);
  const [togglingAuto, setTogglingAuto] = useState(false);

  const money = useCallback(
    (v: number) => {
      const n = formatAmount(v, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      return isRTL ? `${n} ر.س` : `SAR ${n}`;
    },
    [isRTL],
  );

  const fmtDate = useCallback(
    (iso: string) => new Intl.DateTimeFormat(latnLocale(locale), { dateStyle: 'medium' }).format(new Date(iso)),
    [locale],
  );

  const loadInvoices = useCallback(
    async (p: number) => {
      if (!token) return;
      setLoading(true);
      setLoadError(false);
      try {
        const res = await invoiceService.getInvoices(token, { page: p, pageSize: 10 });
        setInvoices(res.data);
        setTotalPages(res.meta.totalPages);
        setPage(res.meta.page);
      } catch {
        setLoadError(true);
      } finally {
        setLoading(false);
      }
    },
    [token],
  );

  useEffect(() => {
    loadInvoices(1);
  }, [loadInvoices]);

  useEffect(() => {
    if (!token) return;
    invoiceService
      .getBillingProfile(token)
      .then((res) => setProfile({ ...EMPTY_PROFILE, ...res.data }))
      .catch(() => {});
    invoiceService
      .getSubscription(token)
      .then((res) => setSubscription(res.data))
      .catch(() => {});
  }, [token]);

  const onToggleAutoRenew = async () => {
    if (!token || !subscription) return;
    setTogglingAuto(true);
    try {
      const res = await invoiceService.setAutoRenew(token, !subscription.autoRenew);
      setSubscription(res.data);
      showToast(t('billing:subscription.saved'), 'success');
    } catch {
      showToast(t('billing:subscription.saveError'), 'error');
    } finally {
      setTogglingAuto(false);
    }
  };

  const onView = async (inv: InvoiceSummary) => {
    if (!token) return;
    setBusyId(inv.id);
    try {
      await invoiceService.openInvoiceHtml(token, inv.id);
    } catch {
      showToast(t('billing:invoices.pdfError'), 'error');
    } finally {
      setBusyId(null);
    }
  };

  const onDownload = async (inv: InvoiceSummary) => {
    if (!token) return;
    setBusyId(inv.id);
    try {
      const kind = await invoiceService.downloadInvoicePdf(token, inv.id, inv.invoiceNumber);
      if (kind === 'html') showToast(t('billing:invoices.pdfFallback'), 'success');
    } catch {
      showToast(t('billing:invoices.pdfError'), 'error');
    } finally {
      setBusyId(null);
    }
  };

  const saveProfile = async (e: FormEvent) => {
    e.preventDefault();
    if (!token) return;
    setSavingProfile(true);
    try {
      const res = await invoiceService.updateBillingProfile(token, profile);
      setProfile({ ...EMPTY_PROFILE, ...res.data });
      showToast(t('billing:profile.saved'), 'success');
    } catch {
      showToast(t('billing:profile.saveError'), 'error');
    } finally {
      setSavingProfile(false);
    }
  };

  const field = (key: keyof BillingProfile, value: string) => setProfile((p) => ({ ...p, [key]: value }));
  const inputCls =
    'w-full rounded-xl border border-gray-200 px-3 py-2 text-sm text-gray-900 outline-none focus:border-[#004D40] focus:ring-1 focus:ring-[#004D40]';
  const labelCls = 'block text-xs font-medium text-gray-500 mb-1';

  return (
    <div className="min-h-screen bg-white" dir={i18n.dir()}>
      <Navigation />

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {/* Header */}
        <div className="mb-6">
          <Link to="/account" className="text-sm text-[#004D40] hover:underline inline-flex items-center gap-1">
            <i className={`ri-arrow-${isRTL ? 'right' : 'left'}-line`} />
            {t('billing:backToAccount')}
          </Link>
          <h1 className="mt-2 text-2xl font-bold text-[var(--pv-color-primary)]">{t('billing:pageTitle')}</h1>
          <p className="text-sm text-gray-500 mt-1">{t('billing:pageDescription')}</p>
        </div>

        {/* Subscription */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base font-semibold text-[var(--pv-color-primary)]">{t('billing:subscription.title')}</h2>
            <i className="ri-vip-crown-2-line text-2xl text-[#004D40]" />
          </div>
          {subscription ? (
            <div className="flex flex-col gap-4">
              <div className="flex flex-wrap items-center gap-x-8 gap-y-3">
                <div>
                  <div className="text-xs text-gray-500">{t('billing:subscription.plan')}</div>
                  <div className="font-semibold text-gray-900">
                    {subscription.planName || '-'}
                    <span className="text-gray-400 text-xs font-normal">
                      {' · '}
                      {t(subscription.billingCycle === 'yearly' ? 'billing:subscription.cycleYearly' : 'billing:subscription.cycleMonthly')}
                    </span>
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-500">{t('billing:subscription.status')}</div>
                  <span className={`inline-block mt-0.5 px-2 py-0.5 rounded-full text-xs font-medium ${SUB_STATUS_STYLES[subscription.status]}`}>
                    {t(`billing:subscription.statusValues.${subscription.status}`)}
                  </span>
                </div>
                <div>
                  <div className="text-xs text-gray-500">{t('billing:subscription.nextRenewal')}</div>
                  <div className="font-medium text-gray-900">{fmtDate(subscription.nextBillingAt)}</div>
                </div>
              </div>

              <div className="flex flex-wrap items-center justify-between gap-3 pt-3 border-t border-gray-50">
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    className="accent-[#004D40] w-4 h-4"
                    checked={subscription.autoRenew}
                    onChange={onToggleAutoRenew}
                    disabled={togglingAuto}
                  />
                  <span className="text-sm text-gray-700">{t('billing:subscription.autoRenew')}</span>
                </label>
                <Link to="/membership">
                  <Button size="sm" variant={subscription.status === 'ACTIVE' ? 'outline' : 'primary'}>
                    {subscription.status === 'ACTIVE' ? t('billing:subscription.manage') : t('billing:subscription.renewNow')}
                  </Button>
                </Link>
              </div>

              {!subscription.autoRenewLive && subscription.autoRenew && (
                <p className="text-xs text-amber-600 bg-amber-50 rounded-lg px-3 py-2">{t('billing:subscription.autoRenewPending')}</p>
              )}
            </div>
          ) : (
            <div className="py-6 text-center">
              <p className="font-medium text-gray-700">{t('billing:subscription.none')}</p>
              <p className="text-sm text-gray-400 mb-3">{t('billing:subscription.noneDescription')}</p>
              <Link to="/membership">
                <Button size="sm">{t('billing:subscription.browsePlans')}</Button>
              </Link>
            </div>
          )}
        </div>

        {/* Invoices */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-base font-semibold text-[var(--pv-color-primary)]">{t('billing:invoices.title')}</h2>
              <p className="text-xs text-gray-500 mt-0.5">{t('billing:invoices.description')}</p>
            </div>
            <i className="ri-receipt-line text-2xl text-[#004D40]" />
          </div>

          {loading ? (
            <div className="py-10 text-center text-gray-400 text-sm">
              <i className="ri-loader-4-line animate-spin text-xl" />
            </div>
          ) : loadError ? (
            <div className="py-10 text-center">
              <p className="text-sm text-gray-500 mb-3">{t('billing:invoices.loadError')}</p>
              <Button variant="outline" size="sm" onClick={() => loadInvoices(page)}>
                {t('billing:invoices.retry')}
              </Button>
            </div>
          ) : invoices.length === 0 ? (
            <div className="py-12 text-center">
              <i className="ri-file-list-3-line text-4xl text-gray-300" />
              <p className="mt-3 font-medium text-gray-700">{t('billing:invoices.empty')}</p>
              <p className="text-sm text-gray-400">{t('billing:invoices.emptyDescription')}</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-gray-500 border-b border-gray-100">
                      <th className="text-start font-medium py-2 px-2">{t('billing:invoices.invoice')}</th>
                      <th className="text-start font-medium py-2 px-2">{t('billing:invoices.date')}</th>
                      <th className="text-start font-medium py-2 px-2 hidden sm:table-cell">{t('billing:invoices.items')}</th>
                      <th className="text-start font-medium py-2 px-2">{t('billing:invoices.total')}</th>
                      <th className="text-start font-medium py-2 px-2">{t('billing:invoices.status')}</th>
                      <th className="text-end font-medium py-2 px-2">{t('billing:invoices.actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoices.map((inv) => {
                      const first = inv.items[0];
                      const firstLabel = first ? (isRTL ? first.descriptionAr || first.description : first.description) : '-';
                      const extra = inv.lineCount - 1;
                      return (
                        <tr key={inv.id} className="border-b border-gray-50 hover:bg-gray-50/60">
                          <td className="py-3 px-2 font-mono text-xs text-gray-700 whitespace-nowrap">{inv.invoiceNumber}</td>
                          <td className="py-3 px-2 text-gray-600 whitespace-nowrap">{fmtDate(inv.issuedAt)}</td>
                          <td className="py-3 px-2 text-gray-600 hidden sm:table-cell max-w-[220px]">
                            <span className="truncate inline-block max-w-[180px] align-middle">{firstLabel}</span>
                            {extra > 0 && (
                              <span className="text-gray-400"> {t('billing:invoices.moreItems', { count: extra })}</span>
                            )}
                          </td>
                          <td className="py-3 px-2 font-semibold text-gray-900 whitespace-nowrap">{money(inv.totalSar)}</td>
                          <td className="py-3 px-2">
                            <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_STYLES[inv.status]}`}>
                              {t(`billing:status.${inv.status}`)}
                            </span>
                          </td>
                          <td className="py-3 px-2">
                            <div className="flex items-center justify-end gap-2">
                              <button
                                onClick={() => onView(inv)}
                                disabled={busyId === inv.id}
                                className="text-xs px-2.5 py-1 rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                              >
                                <i className="ri-eye-line" /> {t('billing:invoices.view')}
                              </button>
                              <button
                                onClick={() => onDownload(inv)}
                                disabled={busyId === inv.id}
                                className="text-xs px-2.5 py-1 rounded-lg bg-[#004D40] text-white hover:bg-[#003930] disabled:opacity-50"
                              >
                                <i className="ri-download-2-line" /> {t('billing:invoices.download')}
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4 text-sm">
                  <span className="text-gray-500">{t('billing:invoices.page', { page, total: totalPages })}</span>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => loadInvoices(page - 1)} disabled={page <= 1}>
                      {t('billing:invoices.prev')}
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => loadInvoices(page + 1)} disabled={page >= totalPages}>
                      {t('billing:invoices.next')}
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Billing profile editor */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between mb-1">
            <h2 className="text-base font-semibold text-[var(--pv-color-primary)]">{t('billing:profile.title')}</h2>
            <i className="ri-bank-card-line text-2xl text-[#004D40]" />
          </div>
          <p className="text-xs text-gray-500 mb-4">{t('billing:profile.description')}</p>

          <form onSubmit={saveProfile} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>{t('billing:profile.legalName')}</label>
              <input className={inputCls} value={profile.legalName ?? ''} onChange={(e) => field('legalName', e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>{t('billing:profile.buyerType')}</label>
              <select className={inputCls} value={profile.buyerType} onChange={(e) => field('buyerType', e.target.value)}>
                <option value="INDIVIDUAL">{t('billing:profile.individual')}</option>
                <option value="COMPANY">{t('billing:profile.company')}</option>
              </select>
            </div>
            <div>
              <label className={labelCls}>
                {t('billing:profile.vatNumber')} <span className="text-gray-300">({t('billing:profile.optional')})</span>
              </label>
              <input className={inputCls} value={profile.vatNumber ?? ''} onChange={(e) => field('vatNumber', e.target.value)} inputMode="numeric" />
            </div>
            <div>
              <label className={labelCls}>
                {t('billing:profile.crNumber')} <span className="text-gray-300">({t('billing:profile.optional')})</span>
              </label>
              <input className={inputCls} value={profile.crNumber ?? ''} onChange={(e) => field('crNumber', e.target.value)} />
            </div>
            <div className="sm:col-span-2">
              <label className={labelCls}>{t('billing:profile.address')}</label>
              <input className={inputCls} value={profile.address ?? ''} onChange={(e) => field('address', e.target.value)} />
            </div>
            <div className="sm:col-span-2">
              <label className={labelCls}>{t('billing:profile.billingEmail')}</label>
              <input className={inputCls} type="email" value={profile.billingEmail ?? ''} onChange={(e) => field('billingEmail', e.target.value)} />
            </div>
            <div className="sm:col-span-2 flex items-center justify-between gap-3">
              <p className="text-xs text-gray-400">{t('billing:profile.b2bNote')}</p>
              <Button type="submit" size="sm" disabled={savingProfile}>
                {t('billing:profile.save')}
              </Button>
            </div>
          </form>
        </div>
      </div>

      <Footer />
    </div>
  );
}
